import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Download, Trash2, Ban, FileText, Clock, CheckCircle,
  AlertTriangle, Loader2, Database, ShoppingCart, MessageCircle,
  Users, Activity, Eye
} from 'lucide-react';

const CATEGORY_ICONS = {
  account: Shield, orders: ShoppingCart, activities: Activity,
  conversations: MessageCircle, team: Users,
};

export default function MyDataPage() {
  const { authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const l = (ar, en) => t('common.lang') === 'ar' ? ar : en;
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [consent, setConsent] = useState({ marketing: false, analytics: true, essential: true });
  const [auditLog, setAuditLog] = useState([]);
  const [retentionPolicy, setRetentionPolicy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [objectDialog, setObjectDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dataRes, reqRes, consentRes, logRes, retRes] = await Promise.all([
        axios.get(`${API}/privacy/my-data`, authHeaders()),
        axios.get(`${API}/privacy/requests`, authHeaders()),
        axios.get(`${API}/privacy/consent`, authHeaders()),
        axios.get(`${API}/privacy/audit-log`, authHeaders()),
        axios.get(`${API}/privacy/retention-policy`, authHeaders()),
      ]);
      setData(dataRes.data);
      setRequests(reqRes.data.requests || []);
      setConsent(consentRes.data);
      setAuditLog(logRes.data.logs || []);
      setRetentionPolicy(retRes.data.policy || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/privacy/export`, authHeaders());
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `zouqai-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(l('تم تصدير بياناتك بنجاح', 'Data exported successfully'));
      fetchAll();
    } catch { toast.error(l('فشل التصدير', 'Export failed')); }
    setExporting(false);
  };

  const handleDeleteRequest = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/privacy/delete-request`, {}, authHeaders());
      toast.success(l('تم تقديم طلب الحذف', 'Deletion request submitted'));
      setDeleteDialog(false);
      fetchAll();
    } catch { toast.error(l('فشل الطلب', 'Request failed')); }
    setSubmitting(false);
  };

  const handleObjection = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/privacy/object`, {}, authHeaders());
      toast.success(l('تم تسجيل اعتراضك', 'Objection recorded'));
      setObjectDialog(false);
      fetchAll();
    } catch { toast.error(l('فشل الطلب', 'Request failed')); }
    setSubmitting(false);
  };

  const updateConsent = async (key, value) => {
    const newConsent = { ...consent, [key]: value };
    setConsent(newConsent);
    try {
      await axios.put(`${API}/privacy/consent`, newConsent, authHeaders());
      toast.success(l('تم تحديث الموافقات', 'Consent updated'));
      fetchAll();
    } catch { toast.error(l('فشل التحديث', 'Update failed')); }
  };

  if (loading) return (
    <ModuleLayout moduleKey="settings">
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </ModuleLayout>
  );

  return (
    <ModuleLayout moduleKey="settings">
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="my-data-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h1 className="font-bold text-xl text-foreground" style={{ fontFamily: 'Tajawal, sans-serif' }} data-testid="my-data-title">
              {l('بياناتي وخصوصيتي', 'My Data & Privacy')}
            </h1>
            <p className="text-xs text-muted-foreground">{l('إدارة بياناتك الشخصية وفقاً لنظام PDPL', 'Manage your personal data per PDPL')}</p>
          </div>
        </div>

        {/* Data Overview Cards */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="data-categories">
            {data.data_categories.map(cat => {
              const Icon = CATEGORY_ICONS[cat.category] || Database;
              return (
                <Card key={cat.category} className="border-border/50" data-testid={`data-cat-${cat.category}`}>
                  <CardContent className="p-4 text-center">
                    <Icon className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                    <div className="text-2xl font-bold text-foreground">{cat.count}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{cat.description_ar}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> {l('حقوقك', 'Your Rights')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Export */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center"><Download className="w-4 h-4 text-blue-500" /></div>
                <div>
                  <p className="text-sm font-medium">{l('تصدير بياناتي', 'Export My Data')}</p>
                  <p className="text-[11px] text-muted-foreground">{l('تحميل جميع بياناتك بصيغة JSON', 'Download all your data as JSON')}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="rounded-lg gap-1.5" data-testid="export-data-btn">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {l('تصدير', 'Export')}
              </Button>
            </div>
            {/* Delete */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4 text-red-500" /></div>
                <div>
                  <p className="text-sm font-medium">{l('طلب حذف بياناتي', 'Request Data Deletion')}</p>
                  <p className="text-[11px] text-muted-foreground">{l('حذف حسابك وجميع بياناتك خلال 30 يوم', 'Delete your account and data within 30 days')}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setDeleteDialog(true)} className="rounded-lg gap-1.5 text-red-500 border-red-500/30 hover:bg-red-500/10" data-testid="delete-data-btn">
                <Trash2 className="w-3.5 h-3.5" /> {l('طلب حذف', 'Request')}
              </Button>
            </div>
            {/* Object */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center"><Ban className="w-4 h-4 text-amber-500" /></div>
                <div>
                  <p className="text-sm font-medium">{l('الاعتراض على المعالجة', 'Object to Processing')}</p>
                  <p className="text-[11px] text-muted-foreground">{l('إيقاف معالجة بياناتك لأغراض معينة', 'Stop processing your data for certain purposes')}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setObjectDialog(true)} className="rounded-lg gap-1.5 text-amber-500 border-amber-500/30 hover:bg-amber-500/10" data-testid="object-btn">
                <Ban className="w-3.5 h-3.5" /> {l('اعتراض', 'Object')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Consent Management */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4" /> {l('إدارة الموافقات', 'Consent Management')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium">{l('ملفات أساسية', 'Essential')}</p><p className="text-[11px] text-muted-foreground">{l('ضرورية لعمل المنصة', 'Required for platform functionality')}</p></div>
              <Switch checked disabled data-testid="consent-essential" />
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium">{l('التحليلات', 'Analytics')}</p><p className="text-[11px] text-muted-foreground">{l('تحسين تجربة الاستخدام', 'Improve user experience')}</p></div>
              <Switch checked={consent.analytics} onCheckedChange={v => updateConsent('analytics', v)} data-testid="consent-analytics" />
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium">{l('التسويق', 'Marketing')}</p><p className="text-[11px] text-muted-foreground">{l('رسائل ترويجية وعروض', 'Promotional messages and offers')}</p></div>
              <Switch checked={consent.marketing} onCheckedChange={v => updateConsent('marketing', v)} data-testid="consent-marketing" />
            </div>
          </CardContent>
        </Card>

        {/* Retention Policy */}
        {retentionPolicy.length > 0 && (
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="w-4 h-4" /> {l('سياسة الاحتفاظ بالبيانات', 'Data Retention Policy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {retentionPolicy.map(p => (
                  <div key={p.category} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/30" data-testid={`retention-${p.category}`}>
                    <span className="text-xs font-medium text-foreground">{p.description_ar}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {p.retention_days ? `${Math.round(p.retention_days / 365)} ${l('سنوات', 'years')}` : l('مستمر', 'Active')}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previous Requests */}
        {requests.length > 0 && (
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" /> {l('طلباتي السابقة', 'My Requests')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {requests.map(req => (
                  <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30" data-testid={`request-${req.id}`}>
                    <div className="flex items-center gap-2">
                      {req.type === 'deletion' ? <Trash2 className="w-4 h-4 text-red-500" /> : <Ban className="w-4 h-4 text-amber-500" />}
                      <div>
                        <p className="text-xs font-medium">{req.type === 'deletion' ? l('طلب حذف', 'Deletion Request') : l('اعتراض', 'Objection')}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(req.created_at).toLocaleDateString('ar-SA')}</p>
                      </div>
                    </div>
                    <Badge className={req.status === 'pending' ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}>
                      {req.status === 'pending' ? l('قيد المراجعة', 'Pending') : l('مكتمل', 'Completed')}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Log */}
        {auditLog.length > 0 && (
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4" /> {l('سجل الخصوصية', 'Privacy Log')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {auditLog.map(log => (
                  <div key={log.id} className="flex items-center justify-between p-2 text-xs" data-testid={`audit-${log.id}`}>
                    <span className="text-muted-foreground">{log.details}</span>
                    <span className="text-[10px] text-muted-foreground">{new Date(log.timestamp).toLocaleString('ar-SA')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <AlertTriangle className="w-5 h-5" /> {l('تأكيد طلب الحذف', 'Confirm Deletion Request')}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {l(
                'بتقديم هذا الطلب، سيتم حذف حسابك وجميع بياناتك خلال 30 يوم. هذا الإجراء لا يمكن التراجع عنه. البيانات المطلوبة قانونياً (مثل الفواتير) ستُحتفظ وفقاً للمتطلبات النظامية.',
                'By submitting this request, your account and all data will be deleted within 30 days. This action cannot be undone. Legally required data (e.g., invoices) will be retained per regulatory requirements.'
              )}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(false)}>{l('إلغاء', 'Cancel')}</Button>
              <Button variant="destructive" onClick={handleDeleteRequest} disabled={submitting} data-testid="confirm-delete-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : l('تأكيد الحذف', 'Confirm Deletion')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Object Confirmation Dialog */}
        <Dialog open={objectDialog} onOpenChange={setObjectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-500">
                <Ban className="w-5 h-5" /> {l('تأكيد الاعتراض', 'Confirm Objection')}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {l(
                'بتقديم هذا الاعتراض، سنراجع طلبك وقد نوقف معالجة بياناتك لأغراض غير أساسية. سيتم الرد خلال 30 يوم عمل.',
                'By submitting this objection, we will review your request and may stop processing your data for non-essential purposes. We will respond within 30 business days.'
              )}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setObjectDialog(false)}>{l('إلغاء', 'Cancel')}</Button>
              <Button onClick={handleObjection} disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="confirm-object-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : l('تأكيد الاعتراض', 'Confirm Objection')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
